import type { IncomingMessage } from 'node:http';
import { DEFAULT_LOCALE, isRtlLocale, loadLocaleMessages, matchLocale, type Locale } from './i18n';

/**
 * A page that speaks a language of its OWN, decided by the server for this reader, rather than the reader's chrome
 * language (the Settings pick, the account language, Accept-Language — `resolveInitialLocale`).
 *
 * The one such page today is a feedback round's public page (`/s/<token>`, feedback mode). Its rule (2026-10-30): what a
 * person asked for feedback sees is in the round OWNER's chat language, unless that person is a Symphony user, who gets
 * their own — and the SERVICE resolves which (`PublicFeedbackRound.language`), off the token and the signed-in caller.
 * Nothing here reads a language from the request: not a query parameter, not a cookie, not Accept-Language, so a reader
 * cannot choose it and the owner's choice is the one that holds.
 *
 * Three places carry it, because three things render the page:
 * - the page's own i18n instance (`createInstance` under a nested `I18nextProvider`, seeded with {@link pageMessagesFor}),
 *   so the body, the <title> and the OG tags are in that language on the server render;
 * - `<html lang dir>` in the served HTML: `getServerSideProps` stamps the request ({@link stampPageLocale}) and
 *   `_document` reads it back ({@link stampedPageLocale}) — `_document` sees the request, never the page's props;
 * - `<html lang dir>` after hydration: `_app`'s effect re-applies the chrome language on mount, so it defers to the
 *   page's `pageLocale` prop ({@link pageLocaleFromProps}) when there is one.
 */

/** The service's language → a locale we ship. Empty, unknown or unshipped is English. */
export function pageLocaleOf(language: string | null | undefined): Locale {
  return matchLocale(language) ?? DEFAULT_LOCALE;
}

/** `<html lang dir>` for a locale: Hebrew and Arabic are right-to-left. */
export function htmlLangDir(locale: Locale): { lang: Locale; dir: 'ltr' | 'rtl' } {
  return { lang: locale, dir: isRtlLocale(locale) ? 'rtl' : 'ltr' };
}

/**
 * The bundle the page ships for a non-English locale, cut to its own keys so `__NEXT_DATA__` does not carry a second
 * whole catalog beside `_app`'s. Null for English (statically bundled, and the fallback for anything missing) and for a
 * bundle that failed to load (the page then renders English rather than raw keys).
 */
export async function pageMessagesFor(
  locale: Locale,
  prefixes: readonly string[],
  load: (locale: Locale) => Promise<Record<string, string>> = loadLocaleMessages,
): Promise<Record<string, string> | null> {
  if (locale === DEFAULT_LOCALE) return null;
  try {
    const all = await load(locale);
    return Object.fromEntries(Object.entries(all).filter(([key]) => prefixes.some((prefix) => key.startsWith(prefix))));
  } catch {
    return null;
  }
}

/** Where on the request the page's locale rides from `getServerSideProps` to `_document`. */
const PAGE_LOCALE = Symbol.for('symphony.pageLocale');

export function stampPageLocale(req: IncomingMessage | object | undefined, locale: Locale): void {
  if (req && typeof req === 'object') (req as Record<symbol, unknown>)[PAGE_LOCALE] = locale;
}

export function stampedPageLocale(req: IncomingMessage | object | undefined): Locale | null {
  const value = req && typeof req === 'object' ? (req as Record<symbol, unknown>)[PAGE_LOCALE] : undefined;
  return typeof value === 'string' ? matchLocale(value) : null;
}

/** The page's own locale from its props (`pageLocale`), validated; null for every page that follows the chrome. */
export function pageLocaleFromProps(pageProps: unknown): Locale | null {
  const value = pageProps && typeof pageProps === 'object' ? (pageProps as { pageLocale?: unknown }).pageLocale : undefined;
  return typeof value === 'string' ? matchLocale(value) : null;
}
